// One place that turns a failed request into words for the user.
//
// Works with axios errors, fetch responses wrapped in ApiError, plain
// network failures and SSE `error` events. The API's error body is
//   { error: { code, message, requestId } }
// but older endpoints still send `error` as a string, so both are read.
//
//   const info = describeApiError(err, { flow: "grade" });
//   info.message   → copy to show
//   info.kind      → offline | network | unauthenticated | forbidden |
//                    rate_limited | client | server
//   info.requestId → small "Ref" for support
//   info.retryable → whether a Try again button makes sense

export class ApiError extends Error {
  constructor({ status, code, message, requestId, body } = {}) {
    super(message || `Request failed${status ? ` (${status})` : ""}`);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.body = body;
  }
}

// Build an ApiError from a non-OK fetch Response.
export async function apiErrorFromResponse(response) {
  let body = null;
  let text = "";
  try {
    text = await response.text();
    body = text ? JSON.parse(text) : null;
  } catch (_) {
    body = null;
  }
  const err = body && typeof body.error === "object" && body.error ? body.error : {};
  const apiError = new ApiError({
    status: response.status,
    code: err.code,
    message: err.message || (body && (typeof body.error === "string" ? body.error : body.message)),
    requestId: err.requestId || response.headers?.get?.("x-request-id") || undefined,
    body,
  });
  apiError.rawBody = text;
  return apiError;
}

const AI_CODES = new Set(["AI_UNAVAILABLE", "VOICE_UNAVAILABLE"]);

export const COPY = {
  offline: "You're offline. Check your connection, then try again.",
  offlineAutoRetry: "You're offline. We'll retry when you're back.",
  network: "We couldn't reach Hugo. Check your connection and try again.",
  unauthenticated: "Your session has expired. Sign in again to carry on.",
  forbidden: "You don't have access to that.",
  rateLimited: "Too many tries. Wait a minute, then try again.",
  server: "Something broke on our side. Please try again in a moment.",
  client: "Something in that request wasn't right. Please check it and try again.",
};

// Copy for 5xx / AI failures in the flows people care most about. Each
// line says what happened to their work and what to do next.
const FLOW_COPY = {
  upload: "We couldn't upload your recording. It's still here, so you can try again.",
  transcribe: "We couldn't turn your recording into text. It's still here, so you can try again.",
  answer: "We couldn't send your answer for feedback. Your recording is still here, so you can try again.",
  grade: "Hugo couldn't grade that just now. Your answer is saved. Try again.",
  chat: "Your message didn't send. Try again.",
  signin: "We couldn't sign you in just now. Please try again in a moment.",
  signup: "We couldn't create your account just now. Please try again in a moment.",
  checkout: "We couldn't confirm your payment just now. Please refresh in a minute; if you were charged, your plan will show up shortly.",
  cv: "We couldn't read your CV just now. Your file wasn't changed, so you can try again.",
  demo: "Hugo couldn't grade that one just now. Try again in a moment.",
};

// What happened to their work, added to "couldn't reach Hugo" copy.
const KEEP = {
  upload: "Your recording is still here.",
  transcribe: "Your recording is still here.",
  answer: "Your recording is still here.",
  grade: "Your answer is saved.",
  chat: "Your message is still in the box.",
  cv: "Your file wasn't changed.",
};

// Some codes always mean a specific step failed, whatever page asked.
const CODE_FLOW = {
  TRANSCRIPTION_FAILED: "transcribe",
  UPLOAD_FAILED: "upload",
};

// Work out the failing step from the endpoint when the caller didn't say.
const URL_FLOW = [
  [/upload-video|get-presigned-url|amazonaws\.com/, "upload"],
  [/fast-audio-transcription|\/transcribe/, "transcribe"],
  [/save-answer|save-followup-answer/, "answer"],
  [/\/chatbot\/hugo/, "chat"],
  [/read-resume/, "cv"],
  [/\/demo\//, "demo"],
];

function flowFromUrl(url) {
  if (!url) return undefined;
  const hit = URL_FLOW.find(([re]) => re.test(url));
  return hit ? hit[1] : undefined;
}

function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

// The string message an error body carries, whichever shape it has.
export function serverMessage(data) {
  if (!data) return undefined;
  if (typeof data.error === "string") return data.error;
  if (data.error && typeof data.error.message === "string") return data.error.message;
  if (typeof data.message === "string") return data.message;
  return undefined;
}

// Drop-in replacement for `err.response?.data?.error || fallback` that
// never returns an object.
export function apiErrorMessage(err, fallback) {
  const data = err && err.response ? err.response.data : err;
  return serverMessage(data) || fallback;
}

function parts(err) {
  if (!err) return {};
  if (err instanceof ApiError) {
    return { status: err.status, code: err.code, requestId: err.requestId, data: err.body, message: err.message };
  }
  if (err.isAxiosError || err.response || err.config) {
    const data = err.response && err.response.data;
    const body = data && typeof data.error === "object" ? data.error : {};
    const headers = (err.response && err.response.headers) || {};
    const sent = (err.config && err.config.headers) || {};
    return {
      status: err.response ? err.response.status : undefined,
      code: body.code || (data && data.code),
      requestId: body.requestId || headers["x-request-id"] || sent["X-Request-Id"],
      data,
      url: err.config && err.config.url,
      noResponse: !err.response,
      canceled: err.code === "ERR_CANCELED" || err.name === "CanceledError",
    };
  }
  // Errors raised from SSE `error` events carry code/requestId directly.
  // fetch() rejects with a TypeError when the server can't be reached.
  const network = err instanceof TypeError || /Failed to fetch|NetworkError|Load failed|network error/i.test(err.message || "");
  return {
    status: err.status,
    code: err.code,
    requestId: err.requestId,
    data: err.status ? { message: err.message } : undefined,
    network,
  };
}

export function describeApiError(err, { flow } = {}) {
  const p = parts(err);
  const stepFlow = flowFromUrl(p.url) || CODE_FLOW[p.code] || flow;
  const base = { status: p.status, code: p.code, requestId: p.requestId, flow: stepFlow };

  if (p.canceled) return { ...base, kind: "canceled", message: "", retryable: false };
  if (isOffline()) return { ...base, kind: "offline", message: COPY.offline, retryable: true };
  if (p.noResponse || p.network) {
    const keep = KEEP[stepFlow];
    return { ...base, kind: "network", message: keep ? `${COPY.network} ${keep}` : COPY.network, retryable: true };
  }
  if (p.status === 401) return { ...base, kind: "unauthenticated", message: COPY.unauthenticated, retryable: false };
  if (p.status === 402 || p.status === 403) {
    return { ...base, kind: "forbidden", message: serverMessage(p.data) || COPY.forbidden, retryable: false };
  }
  if (p.status === 429 || p.code === "RATE_LIMITED") return { ...base, kind: "rate_limited", message: COPY.rateLimited, retryable: true };
  if (p.status && p.status < 500) {
    return { ...base, kind: "client", message: serverMessage(p.data) || COPY.client, retryable: false };
  }

  // 5xx, or an SSE failure with a code.
  if (p.code === "PAYMENT_PROVIDER_ERROR" && serverMessage(p.data)) {
    return { ...base, kind: "server", message: serverMessage(p.data), retryable: true };
  }
  let message = stepFlow && FLOW_COPY[stepFlow] ? FLOW_COPY[stepFlow] : COPY.server;
  if (!stepFlow && AI_CODES.has(p.code)) message = "Hugo couldn't respond just now. Please try again in a moment.";
  return { ...base, kind: "server", message, retryable: true };
}

// Short reference shown under error copy.
export function shortRef(requestId) {
  if (!requestId) return "";
  const clean = String(requestId).replace(/^web-/, "");
  return clean.slice(0, 8);
}

export function newRequestId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return `web-${crypto.randomUUID()}`;
  } catch (_) {
    // Fall through.
  }
  return `web-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
}

// Sign-in URL that brings the user back to where they were.
export function signInPath(returnTo) {
  const here = returnTo || (typeof window !== "undefined" ? `${window.location.pathname}${window.location.search}` : "/c");
  return `/auth/login?next=${encodeURIComponent(here)}`;
}

// Only allow same-site relative paths as a post-login destination.
export function safeNextPath(raw) {
  if (!raw || typeof raw !== "string") return null;
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return null;
  if (raw.startsWith("/auth")) return null;
  return raw;
}
